import type {ReactElement} from 'react';
import {useEffect} from 'react';
import React from 'react';
import {GoogleMap} from '@Components/google-map';
import {Button, Size, Type} from '@Components/button';
import {closeModal} from '@Components/modal-container/modal-container-reducer';
import {useDispatch} from 'react-redux';
import useCustomPanelClose from '@Panels/hooks/useCustomPanelClose';
import styles from './google-map-selector-panel-content.module.scss';
import type {GoogleMapSelectorPanelContentProps} from './google-map-selector-panel-content.types';
import {InputFieldSize} from '@Components/input-field-v2/input-field.types';
import {GoogleMapAutocomplete} from '@Components/google-map/components/google-map-autocomplete';
import type {PlaceDetails} from '@Components/google-map/google-map.types';
import {getUserCurrentLocation} from '@Components/google-map/google-map.helper';

export function GoogleMapSelectorPanelContent({
  googleMapAPIKey,
  isMobile,
  onLocationChange,
  selectorPanelCTAOptions,
  defaultSelectedLocation = undefined,
}: GoogleMapSelectorPanelContentProps): ReactElement<any> {
  const dispatch = useDispatch();

  const [selectedLocation, setSelectedLocation] = React.useState<PlaceDetails | undefined>(defaultSelectedLocation);

  useEffect(() => {
    if (!defaultSelectedLocation) {
      getUserCurrentLocation(onUpdateLocation);
    }
  }, []);

  const onUpdateLocation = (placeDetails: PlaceDetails | undefined): void => {
    setSelectedLocation(placeDetails);

    if (onLocationChange) {
      onLocationChange(placeDetails);
    }
  };

  const onModalClose = (): void => {
    if (selectorPanelCTAOptions) {
      dispatch(closeModal(selectorPanelCTAOptions.modalId));
    }
  };

  const getCTAOptionsContent = (): ReactElement<any> | undefined => {
    if (!selectorPanelCTAOptions) {
      return undefined;
    }

    const selectedVenuePanelClose = useCustomPanelClose(selectorPanelCTAOptions.panelId, selectorPanelCTAOptions.onSelectedVenuePanelClose);

    return (
      <div className={`flex-row-align-center justify-content-end ${isMobile ? 'spacing-p-b-4' : 'spacing-p-b-6'}`}>
        {selectorPanelCTAOptions.showCloseOption ? (
          <Button onClick={onModalClose} customClasses="spacing-m-r-3" type={Type.SECONDARY} size={Size.MEDIUM} text={window.i18next.t('pmwjs_cancel')} />
        ) : undefined}
        <Button
          disabled={!selectorPanelCTAOptions?.isLocationSelected}
          onClick={selectedVenuePanelClose}
          type={Type.PRIMARY}
          size={Size.MEDIUM}
          text={window.i18next.t('pmwjs_done')}
        />
      </div>
    );
  };

  return (
    <>
      <div className={`${isMobile ? 'spacing-p-t-4 spacing-p-b-4' : 'spacing-p-t-6 spacing-p-b-6'} _full-width`}>
        <div className="spacing-m-b-6">
          <GoogleMapAutocomplete googleMapAPIKey={googleMapAPIKey} onLocationChange={onUpdateLocation} inputFieldSize={InputFieldSize.SMALL} />
        </div>
        <div className={`${styles.mapContainer} radius-8 _overflow-hidden`}>
          <GoogleMap googleMapAPIKey={googleMapAPIKey} defaultLocation={selectedLocation} />
        </div>
      </div>
      {getCTAOptionsContent()}
    </>
  );
}
